import { WonderlandClient } from '@wonderlandcloud/client';
import { Emitter } from '@wonderlandengine/api';
import { UserMessaging } from '../utils/user-messaging.js';
import { UserSettingsStore } from '../utils/user-settings-store.js';

const COMMANDER_URL = 'https://cloud.wonderland.dev';
type AudioHintMode = 'auto' | 'speech' | 'music' | 'none';
type NoiseSuppressionMode = 'default' | 'mild' | 'aggressive';
type AudioRoutingMode = 'premix' | 'split';

interface RuntimeAudioTuning {
    audioRoutingMode: AudioRoutingMode;
    echoCancellation: boolean;
    noiseSuppression: boolean;
    noiseSuppressionMode: NoiseSuppressionMode;
    broadcastDisableProcessing: boolean;
    autoGainControl: boolean;
    enableSenderBitrateCap: boolean;
    speechMaxBitrate: number;
    musicMaxBitrate: number;
    contentHintMode: AudioHintMode;
}

interface RtcInboundAudioSnapshot {
    packetsReceived: number;
    packetsLost: number;
    concealedSamples: number;
}

/**
 * CustomClient
 */
export class CustomClient extends WonderlandClient.WonderlandClient {
    static TypeName = 'CustomClient';

    sharemicrophone: boolean = true;
    headlessmode: boolean = false;
    authToken: string | null = null;
    private roomName: string | null = null;
    private roomPassword: string | null = null;
    private connectionOptions: Partial<WonderlandClient.WonderlandClientOptions> = {};  // Store constructor options
    private serverBaseUrl: string = COMMANDER_URL;  // Will be updated based on connection options
    private connectionStateLoggingEnabled: boolean = true;
    private disconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private fallbackTriggered: boolean = false;
    private consecutiveFailures: number = 0;
    private lastUserStateMessage: string | null = null;
    private connectionStableTime: number = 0;
    private readonly STABLE_CONNECTION_GRACE_PERIOD = 10000; // 10 seconds
    private iceRestartAttempts: number = 0;
    private readonly MAX_ICE_RESTART_ATTEMPTS = 3;
    private pendingRenegotiation: ReturnType<typeof setTimeout> | null = null;
    private isIceRestarting: boolean = false;
    private turnIceServers: RTCIceServer[] | null = null;
    private turnIceServersPromise: Promise<RTCIceServer[] | null> | null = null;
    private readonly TURN_CONFIG_URL = 'https://meetup.b82oh.com/api/turn-credentials';
    private mixedAudioContext: AudioContext | null = null;
    private mixedAudioDestination: MediaStreamAudioDestinationNode | null = null;
    private microphoneInputStream: MediaStream | null = null;
    private broadcastShareAudioStream: MediaStream | null = null;
    private microphoneSourceNode: MediaStreamAudioSourceNode | null = null;
    private broadcastSourceNode: MediaStreamAudioSourceNode | null = null;
    private microphoneGainNode: GainNode | null = null;
    private broadcastGainNode: GainNode | null = null;
    private microphoneAudioSender: RTCRtpSender | null = null;
    private broadcastAudioSender: RTCRtpSender | null = null;
    private broadcastShareAudioGain: number = 1;
    private rtcAudioStatsInterval: ReturnType<typeof setInterval> | null = null;
    private rtcInboundAudioSnapshots: Map<string, RtcInboundAudioSnapshot> = new Map();
    private readonly rtcAudioStatsIntervalMs = 3000;
    private readonly rtcAudioStatsEnabled = true;
    private audioTuning: RuntimeAudioTuning = {
        audioRoutingMode: 'split',
        echoCancellation: true,
        noiseSuppression: true,
        noiseSuppressionMode: 'default',
        broadcastDisableProcessing: true,
        autoGainControl: true,
        enableSenderBitrateCap: true,
        speechMaxBitrate: 128000,
        musicMaxBitrate: 192000,
        contentHintMode: 'auto',
    };

    public onMediasoupMessage: Emitter<[any]> = new Emitter();

    private mediasoupRequestId = 1;
    private mediasoupPending: Map<number, { resolve: (value: any) => void; reject: (reason?: any) => void; timeoutId: ReturnType<typeof setTimeout> }>
        = new Map();

    constructor(userOptions: Partial<WonderlandClient.WonderlandClientOptions> = {}) {
        super(userOptions);
        // Store the connection options so we can access host/port/secure later
        this.connectionOptions = userOptions;
        this.loadAudioTuningSettings();
        this.installAudioTuningRuntimeApi();
        // Monitoring setup happens in createPeerConnection() override
    }

    getAudioTuningSettings(): RuntimeAudioTuning {
        return { ...this.audioTuning };
    }

    setAudioTuningSettings(partial: Partial<RuntimeAudioTuning>) {
        const next: RuntimeAudioTuning = {
            ...this.audioTuning,
            ...partial,
        };

        next.speechMaxBitrate = this.clampBitrate(next.speechMaxBitrate, 16000, 512000, 128000);
        next.musicMaxBitrate = this.clampBitrate(next.musicMaxBitrate, 32000, 512000, 192000);
        next.contentHintMode = this.normalizeContentHintMode(next.contentHintMode);
        next.audioRoutingMode = this.normalizeAudioRoutingMode(next.audioRoutingMode);
        next.noiseSuppressionMode = this.normalizeNoiseSuppressionMode(next.noiseSuppressionMode);

        this.audioTuning = next;
        this.persistAudioTuningSettings();

        void this.applyAudioTuningRuntimeChanges();
        this._debugLog('Applied audio tuning settings', this.audioTuning);
    }

    private async applyAudioTuningRuntimeChanges() {
        await this.refreshMicrophoneInput(this.inputDeviceId);
        await this.syncOutgoingAudioTracks();
    }

    private clampBitrate(value: number, min: number, max: number, fallback: number) {
        if (!Number.isFinite(value)) {
            return fallback;
        }
        if (value < min) {
            return min;
        }
        if (value > max) {
            return max;
        }
        return Math.round(value);
    }

    private normalizeContentHintMode(value: AudioHintMode | string | undefined): AudioHintMode {
        if (value === 'speech' || value === 'music' || value === 'none') {
            return value;
        }
        return 'auto';
    }

    private normalizeAudioRoutingMode(value: AudioRoutingMode | string | undefined): AudioRoutingMode {
        if (value === 'split') {
            return 'split';
        }
        return 'premix';
    }

    private normalizeNoiseSuppressionMode(value: NoiseSuppressionMode | string | undefined): NoiseSuppressionMode {
        if (value === 'mild' || value === 'aggressive') {
            return value;
        }
        return 'default';
    }

    private loadAudioTuningSettings() {
        const parsed = UserSettingsStore.getJson<Partial<RuntimeAudioTuning>>(UserSettingsStore.Keys.AudioTuning);
        if (!parsed) {
            return;
        }

        this.audioTuning = {
            ...this.audioTuning,
            ...parsed,
        };
        this.audioTuning.speechMaxBitrate = this.clampBitrate(this.audioTuning.speechMaxBitrate, 16000, 512000, 128000);
        this.audioTuning.musicMaxBitrate = this.clampBitrate(this.audioTuning.musicMaxBitrate, 32000, 512000, 192000);
        this.audioTuning.contentHintMode = this.normalizeContentHintMode(this.audioTuning.contentHintMode);
        this.audioTuning.audioRoutingMode = this.normalizeAudioRoutingMode(this.audioTuning.audioRoutingMode);
        this.audioTuning.noiseSuppressionMode = this.normalizeNoiseSuppressionMode(this.audioTuning.noiseSuppressionMode);
    }

    private persistAudioTuningSettings() {
        UserSettingsStore.setJson(UserSettingsStore.Keys.AudioTuning, this.audioTuning);
    }

    private installAudioTuningRuntimeApi() {
        if (typeof window === 'undefined') {
            return;
        }

        const self = this;
        (window as any).wecloudAudioTuning = {
            get() {
                return self.getAudioTuningSettings();
            },
            set(partial: Partial<RuntimeAudioTuning>) {
                self.setAudioTuningSettings(partial || {});
                return self.getAudioTuningSettings();
            },
            reset() {
                self.setAudioTuningSettings({
                    audioRoutingMode: 'split',
                    echoCancellation: true,
                    noiseSuppression: true,
                    noiseSuppressionMode: 'default',
                    broadcastDisableProcessing: true,
                    autoGainControl: true,
                    enableSenderBitrateCap: true,
                    speechMaxBitrate: 128000,
                    musicMaxBitrate: 192000,
                    contentHintMode: 'auto',
                });
                return self.getAudioTuningSettings();
            },
        };
    }

    private buildMicrophoneAudioConstraints(deviceId?: string): MediaTrackConstraints {
        const constraints: any = {
            deviceId: deviceId || this.inputDeviceId || void 0,
            echoCancellation: this.audioTuning.echoCancellation,
            noiseSuppression: this.audioTuning.noiseSuppression,
            autoGainControl: this.audioTuning.autoGainControl,
        };

        if (this.audioTuning.noiseSuppression) {
            if (this.audioTuning.noiseSuppressionMode === 'mild') {
                constraints.advanced = [
                    { googNoiseSuppression: true },
                    { googNoiseSuppression2: false },
                    { googTypingNoiseDetection: false },
                ];
            } else if (this.audioTuning.noiseSuppressionMode === 'aggressive') {
                constraints.advanced = [
                    { googNoiseSuppression: true },
                    { googNoiseSuppression2: true },
                    { googTypingNoiseDetection: true },
                ];
            }
        }

        return constraints;
    }

    private async applyBroadcastAudioTrackConstraints(stream: MediaStream | null) {
        if (!stream || !this.audioTuning.broadcastDisableProcessing) {
            return;
        }

        for (const track of stream.getAudioTracks()) {
            if (track.readyState !== 'live') {
                continue;
            }
            try {
                await track.applyConstraints({
                    channelCount: 2,
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false,
                    sampleRate: 48000,
                    sampleSize: 16,
                });
            } catch {
                // ignore unsupported constraints
            }
        }
    }

    /**
     * Override createPeerConnection to use STUN plus optional TURN servers.
     * TURN servers are fetched dynamically and cached before signaling.
     * Let base class handle all event logic - only customize ICE servers.
     */
    override createPeerConnection() {
        this.stopRtcAudioStatsMonitor();

        // Create peer connection with STUN servers and optional TURN servers
        const iceServers: RTCIceServer[] = [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:3478' },
            { urls: 'stun:stun2.l.google.com:19302' },
            { urls: 'stun:stun3.l.google.com:3478' },
            { urls: 'stun:stun4.l.google.com:19302' },
            { urls: 'stun:stun4.l.google.com:3478' },
        ];

        if (this.turnIceServers?.length) {
            iceServers.push(...this.turnIceServers);
        }

        this.peerConnection = new RTCPeerConnection({
            iceServers,
            bundlePolicy: 'balanced',
        });
        
        // Attach all base class handlers by calling the base class's handler setup
        // The base class will attach onicegatheringstatechange, onicecandidate, onsignalingstatechange, 
        // ontrack, onnegotiationneeded, onconnectionstatechange handlers
        (this as any).attachPeerConnectionHandlers?.();
        
        // If base class doesn't expose attachPeerConnectionHandlers, manually call handlers
        if (!(this as any).attachPeerConnectionHandlers) {
            // Manually set up handlers that base class would normally set up
            this.peerConnection.onicegatheringstatechange = (event) => {
                this._debugLog(
                    'ice gathering change',
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    //@ts-ignore
                    event.target.iceGatheringState
                );
            };
            
            this.peerConnection.onicecandidate = (event) => {
                this._debugLog('iceevent', event);
                if (event.candidate) {
                    this.sendViaWsInternal({
                        name: 'candidate' as any,
                        data: {
                            candidate: event.candidate,
                        },
                    });
                }
            };
            
            this.peerConnection.onsignalingstatechange = () => {
                this._debugLog(
                    'received signalling state change event',
                    this.peerConnection?.signalingState
                );
                if (
                    this.peerConnection &&
                    (this.peerConnection.signalingState === 'have-remote-offer' ||
                        this.peerConnection.signalingState === 'stable')
                ) {
                    (this as any).candidates.forEach((can: RTCIceCandidate) => {
                        if (this.peerConnection) {
                            this.peerConnection
                                .addIceCandidate(can)
                                .then((val) => console.log('added ice candidate', val, can))
                                .catch((err) => console.log('could not add candidate', err));
                        }
                    });
                    (this as any).candidates.length = 0;
                }
            };
            
            this.peerConnection.ontrack = async (event: RTCTrackEvent) => {
                this._debugLog('received track', event.track.kind, event.track);
                
                if (event.track.kind === 'audio') {
                    // Audio track handling - only process if audio is enabled
                    if (!this.audio) return;
                    (this as any).gotRemoteMediaStream(event);
                }
            };
            
            this.peerConnection.onnegotiationneeded = async (event) => {
                this._debugLog('received negotiation needed event', event);
                if (this.peerConnection?.signalingState !== 'stable') {
                    return;
                }
                (this as any).makingOffer = true;
                await (this as any).startNegotiation();
                (this as any).makingOffer = false;
            };
            
            this.peerConnection.onconnectionstatechange = () => {
                this._debugLog('connection state change', this.peerConnection);
                if (this.peerConnection?.connectionState === 'connected') {
                    // Reset fallback flag when connection recovers
                    this.fallbackTriggered = false;
                    this.clearOldNotifications();
                } else if (this.peerConnection?.connectionState === 'failed') {
                    // Only trigger fallback once per failure cycle
                    if (!this.fallbackTriggered) {
                        this.fallbackTriggered = true;
                        // Don't try to restart ICE - let natural recovery happen
                        // If that doesn't work, fallback to WebSocket
                        console.warn('[CustomClient] Connection failed, will attempt natural recovery');
                        this.notifyUserStatePersistent('Connection to server failed. Attempting to recover...', 60);
                        setTimeout(() => {
                            if (this.peerConnection?.connectionState === 'failed' || 
                                this.peerConnection?.connectionState === 'disconnected') {
                                console.warn('[CustomClient] Natural recovery did not work, falling back to WebSocket');
                                this.webRTCSupported = false;
                                
                                this.clearOldNotifications();
                                this.notifyUserStatePersistent('Connection recovered via fallback.', 5);
                                (this as any).createWSDataConnection();
                            }
                        }, 6000);
                    }
                }
            };
        }

        const existingConnectionStateHandler = this.peerConnection.onconnectionstatechange;
        this.peerConnection.onconnectionstatechange = (event) => {
            try {
                const pc = this.peerConnection;
                if (pc) {
                    existingConnectionStateHandler?.call(pc, event);
                }
            } finally {
                this.onPeerConnectionStateChangedForAudioStats();
            }
        };

        this.startRtcAudioStatsMonitor();
    }



    /**
     * Override connection flow to batch audio track addition with data channel creation.
     * This prevents multiple rapid renegotiations that destabilize the ICE connection.
     * WebRTC automatically batches these - onnegotiationneeded fires once after both are added.
     */
    override async connectAndJoinRoom(data = {}): Promise<number[]> {
        // Extract and store server configuration from connection options
        this.setServerBaseUrl(this.connectionOptions);
        // Capture room from join data so sendStartServerSignal and getUrl can use it
        this.roomName = ((data as any)?.room as string | undefined)?.trim() || null;
        this.roomPassword = ((data as any)?.roomPassword as string | undefined) || null;
        
        if (!this.skipServerStart) {
            const isRunning = await this.sendStartServerSignal();
            if (!isRunning) {
                throw Error('server failed to start or is not running');
            }
        }

        if (this.webRTCSupported) {
            await this.loadTurnIceServers();
        }

        const createdObjects = await (this as any).createSignalling(data);
        
        if (this.webRTCSupported) {
            this._debugLog('peer connection created!');
            
            // BATCHED APPROACH: Add audio tracks BEFORE creating data channel
            // WebRTC will automatically fire onnegotiationneeded only once after both are added
            if (this.audio) {
                await this.addLocalAudioTracks();
            }
            
            // Now create data channel - onnegotiationneeded will fire with both audio + data
            const dataChannelPromise = (this as any).createDataChannel();
            const dataChannelTimeoutMs = 8000;
            const dataChannelTimeout = new Promise<void>((_, reject) => {
                setTimeout(() => reject(new Error('datachannel-timeout')), dataChannelTimeoutMs);
            });

            try {
                await Promise.race([dataChannelPromise, dataChannelTimeout]);
                this._debugLog('datachannel created');
            } catch (err) {
                console.warn('[CustomClient] datachannel did not open, falling back to websockets', err);
                (this as any).forceWebSocketFallback?.('datachannel-timeout');
                await (this as any).createWSDataConnection();
                this.sendViaWs({
                    type: 'websocketsfallback',
                    data: {},
                });
            }
        } else {
            await (this as any).createWSDataConnection();
            this.sendViaWs({
                type: 'websocketsfallback',
                data: {}
            });
        }
        
        return createdObjects;
    }

    /**
     * Override to append the room name as a query parameter when set.
     * Ensures WebSocket connections are routed to the correct room's game server.
     */
    override getUrl(): string {
        const base = super.getUrl();
        const params: string[] = [];
        if (this.roomName) {
            params.push(`room=${encodeURIComponent(this.roomName)}`);
        }
        if (this.roomPassword) {
            params.push(`rp=${encodeURIComponent(this.roomPassword)}`);
        }
        if (this.authToken) {
            params.push(`auth=${encodeURIComponent(this.authToken)}`);
        }
        if (params.length === 0) return base;
        const sep = base.includes('?') ? '&' : '?';
        return `${base}${sep}${params.join('&')}`;
    }

    /**
     * Override signaling setup to tolerate Blob/ArrayBuffer message payloads.
     */
    override async createSignalling(data: any): Promise<number[]> {
        const url = this.getUrl();
        this._debugLog(`connecting WS to ${url}`);
        this.ws = new WebSocket(url);
        this.ws.binaryType = 'arraybuffer';
        let connected = false;
        let joinAttempts = 0;
        let joinRetryTimer: ReturnType<typeof setInterval> | null = null;

        const stopJoinRetries = () => {
            if (joinRetryTimer) {
                clearInterval(joinRetryTimer);
                joinRetryTimer = null;
            }
        };

        const sendJoinAttempt = (reason: string): boolean => {
            if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
                return false;
            }

            joinAttempts += 1;
            try {
                this.sendViaWsInternal({
                    name: 'join' as any,
                    custom: { ...data },
                });
                this._debugLog(`join sent (attempt ${joinAttempts}, reason=${reason})`);
                return true;
            } catch (err) {
                console.error(`[CustomClient] Failed to send join (attempt ${joinAttempts}, reason=${reason})`, err);
                return false;
            }
        };

        return new Promise((resolve, reject) => {
            this.ws?.addEventListener('open', async (event) => {
                this._debugLog('WebSocket connected, signaling ready', event);
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                this.pingInterval = setInterval(() => {
                    this.sendViaWsInternal({ name: 'ping' as any, data: {} });
                }, 5000);

                // Send join first so room entry does not depend on RTC setup success.
                if (!sendJoinAttempt('open')) {
                    reject(new Error('failed-to-send-join'));
                    return;
                }

                // Retry join a few times until joinack arrives to survive transient drops.
                joinRetryTimer = setInterval(() => {
                    if (connected) {
                        stopJoinRetries();
                        return;
                    }
                    if (joinAttempts >= 4) {
                        stopJoinRetries();
                        return;
                    }
                    sendJoinAttempt('retry');
                }, 700);

                if (this.webRTCSupported) {
                    try {
                        console.log('Creating peer connection', event);
                        this.createPeerConnection();
                    } catch (err) {
                        console.warn('[CustomClient] createPeerConnection failed, continuing with WS fallback', err);
                        this.webRTCSupported = false;
                    }
                } else {
                    console.log('WebRTC not supported, fallback to websockets');
                }
            });

            this.ws?.addEventListener('close', (close) => {
                this._debugLog('WebSocket closed:', close);
                stopJoinRetries();
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                clearInterval(this.pingInterval);
                if (!connected) return reject(close);
                this.cleanupConnection();
            });

            this.ws?.addEventListener(
                'message',
                (async (msgEvt: { data: any }) => {
                    this._debugLog(msgEvt);

                    const raw = msgEvt.data;
                    let text: string | null = null;

                    if (typeof raw === 'string') {
                        text = raw;
                    } else if (raw instanceof Blob) {
                        text = await raw.text();
                    } else if (raw instanceof ArrayBuffer) {
                        try {
                            text = new TextDecoder('utf-8').decode(raw);
                        } catch {
                            text = null;
                        }
                    }

                    if (text === null) {
                        return;
                    }

                    let msg: any;
                    try {
                        msg = JSON.parse(text);
                    } catch (err) {
                        console.error('RECEIVED NON-JSON WS MESSAGE!!', err);
                        return;
                    }

                    this._debugLog('received message from server!', msg);
                    switch (msg.name) {
                        case 'mediasoup': {
                            const requestId = msg.requestId;
                            if (requestId && this.mediasoupPending.has(requestId)) {
                                const pending = this.mediasoupPending.get(requestId);
                                this.mediasoupPending.delete(requestId);
                                if (pending) {
                                    clearTimeout(pending.timeoutId);
                                    if (msg.error) {
                                        pending.reject(new Error(msg.error));
                                    } else {
                                        pending.resolve(msg);
                                    }
                                }
                            } else {
                                this.onMediasoupMessage.notify(msg);
                            }
                            break;
                        }
                        case 'offer': {
                            const description = msg.data?.description;
                            this._debugLog('received', msg.name);
                            this._debugLog(description?.split('\r\n'));
                            this._debugLog(' - Setting remote description');
                            await this.peerConnection?.setRemoteDescription({
                                sdp: description,
                                type: 'offer',
                            });
                            const answer = await this.peerConnection?.createAnswer();
                            if (answer) {
                                await this.peerConnection?.setLocalDescription(answer);
                                this.sendViaWsInternal({
                                    name: 'answer' as any,
                                    data: { description: answer.sdp },
                                });
                                this._debugLog('send answerd');
                            }
                            break;
                        }
                        case 'answer': {
                            const description2 = msg.data?.description;
                            this._debugLog('received', msg.name);
                            this._debugLog(description2?.split('\r\n'));
                            await this.peerConnection?.setRemoteDescription({
                                sdp: description2,
                                type: 'answer',
                            });
                            break;
                        }
                        case 'candidate': {
                            if (msg.data && msg.data.candidate) {
                                if (this.peerConnection?.remoteDescription) {
                                    this.peerConnection
                                        .addIceCandidate(msg.data.candidate)
                                        .then((val) => this._debugLog('added ice candidate', val))
                                        .catch((err) => this._debugLog('could not add candidate', err));
                                } else {
                                    (this as any).candidates.push(msg.data.candidate);
                                }
                            }
                            break;
                        }
                        case 'joinack': {
                            try {
                                connected = true;
                                stopJoinRetries();
                                const parsed = JSON.parse(msg.custom_data);
                                resolve(parsed);
                            } catch (err) {
                                console.error('COULD NOT PARSE JOIN ACK EVENT!!', msg);
                                connected = false;
                                reject(err);
                            }
                            break;
                        }
                        case 'pong':
                            this._debugLog('received pong response');
                            break;
                        case 'restart':
                            this._debugLog('received restart command!');
                            location.reload();
                            break;
                        case 'custom':
                            if (msg && msg.custom_data) {
                                const parsedEvent = JSON.parse(msg.custom_data);
                                this._debugLog('RECEIVED CUSTOM EVENT!!', msg, parsedEvent);
                                this.eventQueue.push(parsedEvent);
                            } else {
                                console.error('received custom event without custom data! cannot be');
                            }
                            break;
                        default:
                            console.error('RECEIVED UNKNOWN EVENT!!', msgEvt);
                            break;
                    }
                }).bind(this)
            );
        });
    }

    /**
     * Override server start request to surface status to the user.
     * Uses the configured server URL (host/port/secure) instead of hardcoded vendor URL
     */
    async sendStartServerSignal(): Promise<boolean> {
        // Build URL from connection options if available
        const baseUrl = this.getServerBaseUrl();
        const apiPrefix = this.getApiPrefixFromServerPath();
        
        const response = await fetch(`${baseUrl}${apiPrefix}/api/servers/start`, {
            body: JSON.stringify({
                path: (this as any).path.replace('/', '').slice(0, -3),
                ...(this.roomName ? { room: this.roomName } : {}),
                ...(this.roomPassword ? { roomPassword: this.roomPassword } : {}),
            }),
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                ...(this.authToken ? { 'Authorization': `Bearer ${this.authToken}` } : {}),
            },
        });
        if (response.status === 200) {
            UserMessaging.instance.clearMessages();
            UserMessaging.instance.notifyState('Server started');
            return true;
        } else if (response.status === 201) {
            UserMessaging.instance.notifyState('Server scheduled for start', 60);
            await new Promise((resolve) => setTimeout(resolve, 10000));
            return this.sendStartServerSignal();
        } else if (response.status === 202) {
            UserMessaging.instance.notifyState('Server is starting', 60);
            await new Promise((resolve) => setTimeout(resolve, 5000));
            return this.sendStartServerSignal();
        } else if (response.status === 403) {
            UserMessaging.instance.notifyState('Room password incorrect');
            throw new Error('ROOM_PASSWORD_INCORRECT');
        } else if (response.status === 429) {
            const body = await response.json().catch(() => ({}));
            let errorCode: string;
            if (body?.error === 'ROOM_LIMIT_REACHED') {
                errorCode = 'ROOM_LIMIT_REACHED';
            } else if (body?.error === 'ROOM_CREATE_COOLDOWN') {
                errorCode = 'ROOM_CREATE_COOLDOWN';
            } else {
                errorCode = 'ROOM_FULL';
            }
            throw new Error(errorCode);
        } else {
            console.error('failed to start server!!', await response.json());
            UserMessaging.instance.notifyState('failed to start server!!');
            return false;
        }
    }

    async sendMediasoupRequest(action: string, data: any = {}, timeoutMs: number = 8000): Promise<any> {
        const ws: WebSocket | undefined = (this as any).ws;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            throw new Error('Mediasoup signaling WebSocket not ready');
        }

        const requestId = this.mediasoupRequestId++;
        const payload = {
            name: 'mediasoup',
            action,
            requestId,
            data,
        };

        return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                this.mediasoupPending.delete(requestId);
                reject(new Error(`Mediasoup request timeout: ${action}`));
            }, timeoutMs);

            this.mediasoupPending.set(requestId, { resolve, reject, timeoutId });
            ws.send(JSON.stringify(payload));
        });
    }

    /**
     * Build the server base URL from connection options
     * Prefers self-hosted configuration over vendor URL
     */
    private getServerBaseUrl(): string {
        // Check if serverBaseUrl was already set by setServerBaseUrl
        if (this.serverBaseUrl !== COMMANDER_URL) {
            return this.serverBaseUrl;
        }
        
        // Otherwise try to compute from connection options
        if (this.connectionOptions?.host) {
            const protocol = this.connectionOptions.secure === false ? 'http' : 'https';
            const port = this.connectionOptions.port ? `:${this.connectionOptions.port}` : '';
            return `${protocol}://${this.connectionOptions.host}${port}`;
        }
        
        // Fallback to vendor default
        return COMMANDER_URL;
    }

    /**
     * Derive REST API path prefix from the WebSocket server path.
     * Example: /MeetupWithAvatars/ws -> /MeetupWithAvatars
     */
    private getApiPrefixFromServerPath(): string {
        const rawPath = this.connectionOptions?.path ?? (this as any).path ?? '';
        const cleaned = rawPath.split('?')[0];
        const parts = cleaned.split('/').filter(Boolean);
        if (parts.length === 0) {
            return '';
        }
        return `/${parts[0]}`;
    }
    
    /**
     * Store server base URL during connection setup
     * Called by connectAndJoinRoom before using sendStartServerSignal
     */
    private setServerBaseUrl(options: any): void {
        if (options?.host) {
            const protocol = options.secure === false ? 'http' : 'https';
            const port = options.port ? `:${options.port}` : '';
            this.serverBaseUrl = `${protocol}://${options.host}${port}`;
            console.log('[CustomClient] Using server URL:', this.serverBaseUrl);
        }
    }

    private async loadTurnIceServers(): Promise<RTCIceServer[] | null> {
        if (this.turnIceServers) return this.turnIceServers;
        if (this.turnIceServersPromise) return this.turnIceServersPromise;

        this.turnIceServersPromise = this.fetchTurnIceServers();
        this.turnIceServers = await this.turnIceServersPromise;
        this.turnIceServersPromise = null;
        return this.turnIceServers;
    }

    private async fetchTurnIceServers(): Promise<RTCIceServer[] | null> {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 4000);

            // Fetch TURN credentials from local REST server
            const turnUrl = `${this.serverBaseUrl}/api/turn-credentials`;

            const response = await fetch(turnUrl, {
                method: 'GET',
                headers: {
                    'accept': 'application/json',
                },
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                console.warn('[CustomClient] TURN config fetch failed:', response.status, 'URL:', turnUrl);
                return null;
            }

            const payload = (await response.json()) as { iceServers?: RTCIceServer[] };
            if (!payload?.iceServers?.length) {
                console.warn('[CustomClient] TURN config missing iceServers');
                return null;
            }

            return payload.iceServers;
        } catch (error) {
            console.warn('[CustomClient] TURN config fetch error:', error);
            return null;
        }
    }

    /**
     * Schedule a fallback to WebSocket if the connection doesn't recover.
     * Uses moderate timeout to allow natural recovery while detecting prolonged failures.
     */
    private scheduleFallback(reason: string) {
        if (this.fallbackTriggered) return;
        this.clearFallbackTimer();
        // 5 second timeout allows transient disconnects to recover naturally
        this.disconnectTimeoutId = setTimeout(() => {
            const state = this.peerConnection?.connectionState;
            if (state === 'disconnected' || state === 'failed') {
                this.recordFailure(`timeout-${reason}`);
            }
        }, 5000);
    }

    private clearFallbackTimer() {
        if (this.disconnectTimeoutId) {
            clearTimeout(this.disconnectTimeoutId);
            this.disconnectTimeoutId = null;
        }
    }

    private notifyUserState(message: string) {
        if (this.lastUserStateMessage === message) return;
        this.lastUserStateMessage = message;
        UserMessaging.instance.notifyState(message);
    }

    private notifyUserStatePersistent(message: string, duration: number) {
        if (this.lastUserStateMessage === message) return;
        this.lastUserStateMessage = message;
        UserMessaging.instance.notifyState(message, duration);
    }

    private clearOldNotifications() {
        UserMessaging.instance.clearMessages();
        this.lastUserStateMessage = null;
    }

    /**
     * Attempt ICE restart to recover transient disconnects.
     * Falls back to full renegotiation if connection is already disconnected.
     */
    private tryRestartIce(reason: string) {
        // Don't try to restart if already in fallback mode or already restarting
        if (this.fallbackTriggered || this.isIceRestarting) return;
        
        // Limit ICE restart attempts to prevent infinite loops
        if (this.iceRestartAttempts >= this.MAX_ICE_RESTART_ATTEMPTS) {
            console.warn(`[CustomClient] Max ICE restart attempts (${this.MAX_ICE_RESTART_ATTEMPTS}) reached, forcing WebSocket fallback`);
            this.forceWebSocketFallback('max-ice-restarts');
            return;
        }
        
        try {
            this.iceRestartAttempts++;
            this.isIceRestarting = true;
            
            // Cancel any pending renegotiations to prevent collisions
            if (this.pendingRenegotiation) {
                clearTimeout(this.pendingRenegotiation);
                this.pendingRenegotiation = null;
                console.warn('[CustomClient] Cancelled pending renegotiation for ICE restart');
            }
            
            // Check if connection is already dead - if so, we can't use restartIce()
            const canRestart = this.peerConnection?.signalingState === 'stable' && 
                               (this.peerConnection?.connectionState === 'connected' ||
                                this.peerConnection?.connectionState === 'disconnected');
            
            if (!canRestart) {
                console.warn('[CustomClient] Cannot restart ICE in signaling state:', this.peerConnection?.signalingState);
                this.forceWebSocketFallback('ice-restart-invalid-state');
                return;
            }
            
            console.warn(`[CustomClient] ICE restart #${this.iceRestartAttempts} requested (${reason})`);
            this.peerConnection!.restartIce();
            
            // Force a renegotiation by manually triggering offers
            setTimeout(async () => {
                if (this.peerConnection?.signalingState === 'stable') {
                    console.warn('[CustomClient] Triggering renegotiation after restartIce()');
                    (this as any).makingOffer = true;
                    try {
                        await (this as any).startNegotiation();
                    } catch (e) {
                        console.error('[CustomClient] Failed to start negotiation after restartIce:', e);
                    }
                    (this as any).makingOffer = false;
                }
            }, 100);
            
            // If ICE restart doesn't work within 10 seconds, try fallback
            setTimeout(() => {
                this.isIceRestarting = false;
                if (this.peerConnection?.connectionState === 'disconnected' || 
                    this.peerConnection?.connectionState === 'failed') {
                    console.warn('[CustomClient] ICE restart did not recover connection, attempting WebSocket fallback');
                    this.forceWebSocketFallback('ice-restart-timeout');
                } else if (this.peerConnection?.connectionState === 'connected') {
                    console.warn('[CustomClient] ICE restart successful');
                    this.connectionStableTime = Date.now(); // Reset grace period after successful restart
                }
            }, 10000);
        } catch (err) {
            this.isIceRestarting = false;
            console.warn('[CustomClient] ICE restart failed', err);
            this.forceWebSocketFallback('ice-restart-error');
        }
    }

    /**
     * Force WebSocket fallback when RTC is unusable.
     */
    private forceWebSocketFallback(reason: string) {
        if (this.fallbackTriggered) return;
        this.fallbackTriggered = true;
        console.warn(`[CustomClient] Forcing WebSocket fallback (${reason})`);
        
        // Clear previous recovery messages and show recovery confirmation
        UserMessaging.instance.clearMessages();
        this.notifyUserState('Connection recovered via fallback.');

        try {
            this.datachannel?.close();
        } catch {
            // ignore
        }

        try {
            this.peerConnection?.close();
        } catch {
            // ignore
        }

        this.webRTCSupported = false;

        const createWs = (this as any).createWSDataConnection;
        if (typeof createWs === 'function') {
            createWs.call(this);
            // Hook into WebSocket message handlers after creation
            setTimeout(() => this.hookWebSocketMessageHandlers(), 100);
        } else {
            console.error('[CustomClient] createWSDataConnection not available for fallback');
        }
    }

    /**
     * Hook into WebSocket message handlers (simplified for production)
     */
    private hookWebSocketMessageHandlers() {
        // Simplified: Only log errors, not all messages
        const wsData = (this as any).wsData;
        if (!wsData) {
            console.warn('[CustomClient] wsData not found for WebSocket fallback');
        }
    }

    /**
     * Track consecutive failures and trigger fallback once threshold is reached.
     */
    private recordFailure(reason: string) {
        // Don't count failures if already in fallback mode
        if (this.fallbackTriggered) return;
        
        this.consecutiveFailures += 1;
        console.warn(`[CustomClient] Failure count ${this.consecutiveFailures} (${reason})`);
        if (this.consecutiveFailures >= 2) {
            this.forceWebSocketFallback(`threshold-${reason}`);
        }
    }

    private resetFailureCount() {
        this.consecutiveFailures = 0;
    }

    /**
     * Log connection diagnostics when issues occur
     */
    private logConnectionDiagnostics() {
        if (!this.peerConnection) return;

        console.warn('[CustomClient] Connection Diagnostics:', {
            connectionState: this.peerConnection.connectionState,
            iceConnectionState: this.peerConnection.iceConnectionState,
            signalingState: this.peerConnection.signalingState,
            dataChannelState: this.datachannel?.readyState || 'Not created'
        });
    }

    private onPeerConnectionStateChangedForAudioStats() {
        const state = this.peerConnection?.connectionState;
        if (!state) {
            return;
        }

        if (state === 'closed' || state === 'failed') {
            this.stopRtcAudioStatsMonitor();
            return;
        }

        if (!this.rtcAudioStatsInterval) {
            this.startRtcAudioStatsMonitor();
        }
    }

    private startRtcAudioStatsMonitor() {
        if (!this.rtcAudioStatsEnabled || this.rtcAudioStatsInterval) {
            return;
        }

        this.rtcAudioStatsInterval = setInterval(() => {
            void this.sampleRtcAudioStats();
        }, this.rtcAudioStatsIntervalMs);
    }

    private stopRtcAudioStatsMonitor() {
        if (this.rtcAudioStatsInterval) {
            clearInterval(this.rtcAudioStatsInterval);
            this.rtcAudioStatsInterval = null;
        }
        this.rtcInboundAudioSnapshots.clear();
    }

    private async sampleRtcAudioStats() {
        const pc = this.peerConnection;
        if (!pc || pc.connectionState !== 'connected') {
            return;
        }

        try {
            const stats = await pc.getStats();
            let currentRttMs = -1;

            stats.forEach((report: any) => {
                if (report.type === 'candidate-pair' && report.state === 'succeeded' && report.nominated) {
                    const rtt = Number(report.currentRoundTripTime);
                    if (Number.isFinite(rtt) && rtt >= 0) {
                        currentRttMs = rtt * 1000;
                    }
                }
            });

            stats.forEach((report: any) => {
                if (report.type !== 'inbound-rtp' || report.kind !== 'audio') {
                    return;
                }

                const key = String(report.ssrc ?? report.id ?? 'inbound-audio');
                const packetsReceived = Number(report.packetsReceived ?? 0);
                const packetsLost = Number(report.packetsLost ?? 0);
                const concealedSamples = Number(report.concealedSamples ?? report.concealedSamplesAudio ?? 0);
                const jitterMs = Number.isFinite(report.jitter) ? Number(report.jitter) * 1000 : 0;

                const prev = this.rtcInboundAudioSnapshots.get(key);
                this.rtcInboundAudioSnapshots.set(key, {
                    packetsReceived,
                    packetsLost,
                    concealedSamples,
                });

                if (!prev) {
                    return;
                }

                const recvDelta = Math.max(0, packetsReceived - prev.packetsReceived);
                const lostDelta = Math.max(0, packetsLost - prev.packetsLost);
                const concealedDelta = Math.max(0, concealedSamples - prev.concealedSamples);
                const totalDelta = recvDelta + lostDelta;
                const lossPct = totalDelta > 0 ? (lostDelta / totalDelta) * 100 : 0;
                const hasIssue = lossPct >= 2 || jitterMs >= 25 || concealedDelta > 0;

                if (hasIssue) {
                    console.warn(
                        `[AudioStats] inbound ssrc=${key} loss=${lossPct.toFixed(1)}% recv+${recvDelta} lost+${lostDelta} jitter=${jitterMs.toFixed(1)}ms concealed+${concealedDelta} rtt=${currentRttMs >= 0 ? currentRttMs.toFixed(1) : 'n/a'}ms`
                    );
                }
            });
        } catch (error) {
            this._debugLog('sampleRtcAudioStats failed', error);
        }
    }

    override recreateMicrophoneTrack(deviceId?: string) {
        void this.refreshMicrophoneInput(deviceId);
    }

    /**
     * Override to prevent XR session from triggering audio renegotiation during initial connection
     */
    override onXRSessionStart(): void {
        // Only recreate microphone track if audio is enabled AND connection is already established
        if (this.audio && this.peerConnection?.connectionState === 'connected') {
            this.recreateMicrophoneTrack();
        }
    }

    override async addLocalAudioTracks() {
        if (this.audioAdded) return;
        if (!this.audioAddingPromise) {
            this.audioAddingPromise = new Promise(async (resolve) => {
                // @ts-ignore
                if (navigator.audioSession) {
                    // @ts-ignore
                    navigator.audioSession.type = 'play-and-record';
                }

                await this.refreshMicrophoneInput(this.inputDeviceId);
                await this.syncOutgoingAudioTracks();
                
                await this.waitForAudioContext();
                this.audioAdded = true;
                this._debugLog('added own media tracks');
                resolve();
            });
        }
        return this.audioAddingPromise;
    }

    setBroadcastShareAudioStream(stream: MediaStream | null) {
        const hasLiveAudio = !!stream?.getAudioTracks().some((track) => track.readyState === 'live');
        this.broadcastShareAudioStream = hasLiveAudio ? stream : null;
        this.rebuildMixedAudioGraph();
        void this.applyBroadcastAudioTrackConstraints(this.broadcastShareAudioStream);
        void this.syncOutgoingAudioTracks();
    }

    setBroadcastShareAudioGain(gain: number) {
        const clampedGain = this.clampMixGain(gain);
        this.broadcastShareAudioGain = clampedGain;
        if (this.broadcastGainNode) {
            this.broadcastGainNode.gain.value = clampedGain;
        }
    }

    private async refreshMicrophoneInput(deviceId?: string) {
        if (this.sharemicrophone) {
            try {
                const media = await navigator.mediaDevices.getUserMedia({
                    audio: this.buildMicrophoneAudioConstraints(deviceId),
                });
                this.releaseStream(this.microphoneInputStream);
                this.microphoneInputStream = media;
            } catch (err) {
                this._debugLog('failed to refresh microphone input', err);
                this.releaseStream(this.microphoneInputStream);
                this.microphoneInputStream = null;
            }
        } else {
            this.releaseStream(this.microphoneInputStream);
            this.microphoneInputStream = null;
        }

        this.rebuildMixedAudioGraph();
        await this.syncOutgoingAudioTracks();
    }

    private replaceOutgoingAudioTrack(): Promise<void> {
        return this.syncOutgoingAudioTracks();
    }

    private async syncOutgoingAudioTracks(): Promise<void> {
        if (!this.peerConnection) {
            return;
        }

        if (this.audioTuning.audioRoutingMode === 'split') {
            await this.syncSplitOutgoingAudioTracks();
            return;
        }

        await this.syncPremixedOutgoingAudioTrack();
    }

    private async syncPremixedOutgoingAudioTrack(): Promise<void> {
        const outgoingAudioTrack = this.getOutgoingMixedAudioTrack() || this.createSilentAudioStream().getAudioTracks()[0];
        const audioSender = this.resolveMicrophoneSender();

        if (!audioSender || !outgoingAudioTrack) {
            if (this.peerConnection && outgoingAudioTrack) {
                const stream = this.mixedAudioDestination?.stream || this.createSilentAudioStream();
                const sender = this.peerConnection.addTrack(outgoingAudioTrack, stream);
                this.microphoneAudioSender = sender;
                await this.applyOutboundAudioQuality(sender, outgoingAudioTrack, 'mixed');
            }
            return;
        }

        this.microphoneAudioSender = audioSender;
        await audioSender.replaceTrack(outgoingAudioTrack);
        await this.applyOutboundAudioQuality(audioSender, outgoingAudioTrack, 'mixed');

        if (this.broadcastAudioSender && this.peerConnection) {
            try {
                this.peerConnection.removeTrack(this.broadcastAudioSender);
            } catch {
                // ignore
            }
            this.broadcastAudioSender = null;
        }
    }

    private async syncSplitOutgoingAudioTracks(): Promise<void> {
        if (!this.peerConnection) {
            return;
        }

        const microphoneTrack = this.getLiveMicrophoneTrack() || this.createSilentAudioStream().getAudioTracks()[0];
        let micSender = this.resolveMicrophoneSender();
        if (!micSender && microphoneTrack) {
            const stream = this.microphoneInputStream || new MediaStream([microphoneTrack]);
            micSender = this.peerConnection.addTrack(microphoneTrack, stream);
            this.microphoneAudioSender = micSender;
        }
        if (micSender && microphoneTrack) {
            await micSender.replaceTrack(microphoneTrack);
            await this.applyOutboundAudioQuality(micSender, microphoneTrack, 'mic');
        }

        const broadcastTrack = this.getOutgoingBroadcastAudioTrack();
        let broadcastSender = this.resolveBroadcastSender();

        if (broadcastTrack) {
            if (!broadcastSender) {
                const stream = this.mixedAudioDestination?.stream || this.broadcastShareAudioStream || new MediaStream([broadcastTrack]);
                broadcastSender = this.peerConnection.addTrack(broadcastTrack, stream);
                this.broadcastAudioSender = broadcastSender;
            }
            await broadcastSender.replaceTrack(broadcastTrack);
            await this.applyOutboundAudioQuality(broadcastSender, broadcastTrack, 'broadcast');
        } else if (broadcastSender) {
            try {
                this.peerConnection.removeTrack(broadcastSender);
            } catch {
                // ignore
            }
            this.broadcastAudioSender = null;
        }
    }

    private getOutgoingMixedAudioTrack(): MediaStreamTrack | null {
        this.ensureMixedAudioGraph();
        return this.mixedAudioDestination?.stream.getAudioTracks()[0] || null;
    }

    private getOutgoingBroadcastAudioTrack(): MediaStreamTrack | null {
        if (!this.hasLiveBroadcastShareAudio()) {
            return null;
        }
        this.ensureMixedAudioGraph();
        return this.mixedAudioDestination?.stream.getAudioTracks()[0] || null;
    }

    private getLiveMicrophoneTrack(): MediaStreamTrack | null {
        return this.microphoneInputStream?.getAudioTracks().find((track) => track.readyState === 'live') || null;
    }

    private resolveMicrophoneSender(): RTCRtpSender | null {
        if (!this.peerConnection) {
            return null;
        }

        const senders = this.peerConnection.getSenders();
        if (this.microphoneAudioSender && senders.includes(this.microphoneAudioSender)) {
            return this.microphoneAudioSender;
        }

        const sender = senders.find((s) => s.track?.kind === 'audio' && s !== this.broadcastAudioSender) || null;
        this.microphoneAudioSender = sender;
        return sender;
    }

    private resolveBroadcastSender(): RTCRtpSender | null {
        if (!this.peerConnection) {
            return null;
        }

        const senders = this.peerConnection.getSenders();
        if (this.broadcastAudioSender && senders.includes(this.broadcastAudioSender)) {
            return this.broadcastAudioSender;
        }

        const sender = senders.find((s) => s.track?.kind === 'audio' && s !== this.microphoneAudioSender) || null;
        this.broadcastAudioSender = sender;
        return sender;
    }

    private ensureMixedAudioGraph() {
        if (this.mixedAudioContext && this.mixedAudioDestination) {
            return;
        }

        this.mixedAudioContext = new AudioContext({
            latencyHint: 'interactive',
            sampleRate: 48000,
        });
        this.mixedAudioDestination = this.mixedAudioContext.createMediaStreamDestination();
    }

    private rebuildMixedAudioGraph() {
        this.ensureMixedAudioGraph();

        if (this.microphoneSourceNode) {
            this.microphoneSourceNode.disconnect();
            this.microphoneSourceNode = null;
        }
        if (this.broadcastSourceNode) {
            this.broadcastSourceNode.disconnect();
            this.broadcastSourceNode = null;
        }
        if (this.microphoneGainNode) {
            this.microphoneGainNode.disconnect();
            this.microphoneGainNode = null;
        }
        if (this.broadcastGainNode) {
            this.broadcastGainNode.disconnect();
            this.broadcastGainNode = null;
        }

        if (!this.mixedAudioContext || !this.mixedAudioDestination) {
            return;
        }

        if (this.audioTuning.audioRoutingMode !== 'split' && this.microphoneInputStream?.getAudioTracks().some((track) => track.readyState === 'live')) {
            this.microphoneSourceNode = this.mixedAudioContext.createMediaStreamSource(this.microphoneInputStream);
            this.microphoneGainNode = this.mixedAudioContext.createGain();
            this.microphoneGainNode.gain.value = 1;
            this.microphoneSourceNode.connect(this.microphoneGainNode);
            this.microphoneGainNode.connect(this.mixedAudioDestination);
        }

        if (this.broadcastShareAudioStream?.getAudioTracks().some((track) => track.readyState === 'live')) {
            this.broadcastSourceNode = this.mixedAudioContext.createMediaStreamSource(this.broadcastShareAudioStream);
            this.broadcastGainNode = this.mixedAudioContext.createGain();
            this.broadcastGainNode.gain.value = this.broadcastShareAudioGain;
            this.broadcastSourceNode.connect(this.broadcastGainNode);
            this.broadcastGainNode.connect(this.mixedAudioDestination);
        }
    }

    private clampMixGain(gain: number): number {
        if (!Number.isFinite(gain)) {
            return 1;
        }
        if (gain < 0) {
            return 0;
        }
        if (gain > 2) {
            return 2;
        }
        return gain;
    }

    private async applyOutboundAudioQuality(
        sender: RTCRtpSender | null,
        track: MediaStreamTrack | null,
        profile: 'mixed' | 'mic' | 'broadcast' = 'mixed'
    ) {
        const hasSharedMediaAudio = this.hasLiveBroadcastShareAudio();
        const hintMode = this.audioTuning.contentHintMode;
        const defaultHint = profile === 'broadcast'
            ? 'music'
            : profile === 'mic'
            ? 'speech'
            : (hasSharedMediaAudio ? 'music' : 'speech');
        const contentHint = hintMode === 'auto'
            ? defaultHint
            : hintMode;

        if (track && 'contentHint' in track && contentHint !== 'none') {
            try {
                (track as MediaStreamTrack).contentHint = contentHint;
            } catch {
                // ignore
            }
        }

        if (!sender || typeof sender.getParameters !== 'function' || typeof sender.setParameters !== 'function') {
            return;
        }

        try {
            const parameters: any = sender.getParameters();
            const encodings = Array.isArray(parameters.encodings) && parameters.encodings.length > 0
                ? [...parameters.encodings]
                : [{} as RTCRtpEncodingParameters];

            const maxBitrate = profile === 'broadcast'
                ? this.audioTuning.musicMaxBitrate
                : profile === 'mic'
                ? this.audioTuning.speechMaxBitrate
                : (hasSharedMediaAudio ? this.audioTuning.musicMaxBitrate : this.audioTuning.speechMaxBitrate);

            encodings[0] = {
                ...encodings[0],
                ...(profile === 'broadcast'
                    ? ({ priority: 'high', networkPriority: 'high' } as Partial<RTCRtpEncodingParameters>)
                    : {}),
                ...(this.audioTuning.enableSenderBitrateCap
                    ? { maxBitrate }
                    : { maxBitrate: undefined }),
            };

            parameters.encodings = encodings;
            await sender.setParameters(parameters as RTCRtpSendParameters);
        } catch (err) {
            this._debugLog('applyOutboundAudioQuality failed', err);
        }
    }

    private hasLiveBroadcastShareAudio() {
        return !!this.broadcastShareAudioStream?.getAudioTracks().some((track) => track.readyState === 'live');
    }

    private releaseStream(stream: MediaStream | null) {
        if (!stream) {
            return;
        }

        for (const track of stream.getTracks()) {
            try {
                track.stop();
            } catch {
                // ignore
            }
        }
    }

    createSilentAudioStream() {
        // Create an audio context
        const audioCtx = new AudioContext();

        // Create a MediaStreamAudioDestinationNode. This node will capture the audio.
        const destination = audioCtx.createMediaStreamDestination();

        // Create a silent audio source.
        const oscillator = audioCtx.createOscillator();
        oscillator.connect(destination);
        //oscillator.start();

        // Get the MediaStreamTrack from the destination node.
        const silentStream = destination.stream;

        return silentStream;
    }

    /**
     * Override send method to fix WebSocket TypedArray serialization issue
     * 
     * The parent class has a bug where TypedArrays sent over WebSocket fallback
     * are not properly converted to ArrayBuffer, causing data corruption.
     * This fix ensures TypedArray views are explicitly converted to their exact
     * ArrayBuffer representation before being sent over WebSocket.
     */
    override send(data: Blob | ArrayBufferView | ArrayBuffer | string) {
        if (this.webRTCSupported) {
            // Use RTCDataChannel (original behavior)
            if (!this.datachannel || this.datachannel.readyState != 'open') return;
            
            if (typeof data === 'string') {
//console.log("Sending RTC string data: " + data);
                this.datachannel.send(data);
            } else if (data instanceof Blob) {
//console.log("Sending RTC Blob data of size: " + data.size);
                this.datachannel.send(data);
            } else if (ArrayBuffer.isView(data)) {
//console.log("Sending RTC TypedArray data of byteLength: " + data.byteLength);
                this.datachannel.send(data as ArrayBufferView<ArrayBuffer>);
            } else if (data instanceof ArrayBuffer) {
//console.log("Sending RTC ArrayBuffer data: " + data);
                this.datachannel.send(data);
            }
        } else {
            // Use WebSocket fallback - properly handle TypedArrays
            if (ArrayBuffer.isView(data)) {
                // Convert TypedArray view to ArrayBuffer slice
                const arrayBuffer = data.buffer.slice(
                    data.byteOffset,
                    data.byteOffset + data.byteLength
                ) as ArrayBuffer;
                this.wsData?.send(arrayBuffer);
            } else {
                // For ArrayBuffer, Blob, or string, send directly
                this.wsData?.send(data as ArrayBuffer);
            }
        }
    }
}
